/**
 * 用户管理页面
 * 管理员可以查看、删除、修改用户角色
 * 超级管理员可以管理其他管理员
 */

import { useState, useEffect } from 'react';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Search,
  UserPlus,
  Shield,
  ShieldCheck,
  User as UserIcon,
  Trash2,
  Edit,
  Clock,
  MapPin,
  RefreshCcw,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { authAPI } from '@/services/authApi';
import {
  getUserList,
  deleteUser,
  updateUserRole,
  updateUserStatus,
  createUser,
  getUserStats,
  updateUserVip,
  getVipLevelInfo,
  isVipExpired,
  formatVipExpireAt,
  type User,
} from '@/services/userManagementApi';
import { Crown, Calendar } from 'lucide-react';

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [vipLevelFilter, setVipLevelFilter] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [vipDialogOpen, setVipDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [newVipLevel, setNewVipLevel] = useState<number>(0);
  const [newVipExpireAt, setNewVipExpireAt] = useState<string>('');
  const [newVipSource, setNewVipSource] = useState<string>('');
  const [stats, setStats] = useState({
    total: 0,
    admin_count: 0,
    user_count: 0,
    active_count: 0,
    inactive_count: 0,
    banned_count: 0,
    vip_free_count: 0,
    vip_vip_count: 0,
    vip_vvip_count: 0,
  });

  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
  });

  const { user: currentUser, accessToken, updateUser } = useAuthStore();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin' || isSuperAdmin;

  // 页面挂载时从服务端刷新当前用户信息，确保角色权限是最新的
  useEffect(() => {
    if (!accessToken) return;
    authAPI.getCurrentUser(accessToken).then((res) => {
      if (res.success && res.data?.user) {
        updateUser(res.data.user);
      }
    }).catch(() => {/* 静默失败，不影响页面使用 */});
  }, []);

  // 检查是否可以操作目标用户
  const canManageUser = (targetUser: User) => {
    // 不能操作自己
    if (targetUser.id === currentUser?.id) return false;
    
    // 超级管理员可以操作所有人
    if (isSuperAdmin) return true;
    
    // 管理员不能操作超级管理员和其他管理员
    if (targetUser.role === 'super_admin' || targetUser.role === 'admin') {
      return false;
    }
    
    return true;
  };

  // 加载用户列表
  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getUserList({
        page,
        pageSize,
        keyword: keyword || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        vipLevel: vipLevelFilter || undefined,
      });

      if (response.success) {
        setUsers(response.data.users);
        setTotal(response.data.pagination.total);
      }
    } catch (error: any) {
      toast.error('加载用户列表失败', {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // 加载统计数据
  const loadStats = async () => {
    try {
      const response = await getUserStats();
      if (response.success) {
        setStats(response.data.stats);
      }
    } catch (error: any) {
      console.error('加载统计数据失败', error);
    }
  };

  useEffect(() => {
    loadUsers();
    loadStats();
  }, [page, roleFilter, statusFilter, vipLevelFilter]);

  // 搜索
  const handleSearch = () => {
    setPage(1);
    loadUsers();
  };

  // 删除用户
  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      const response = await deleteUser(selectedUser.id);
      if (response.success) {
        toast.success('用户已删除');
        setDeleteDialogOpen(false);
        loadUsers();
        loadStats();
      }
    } catch (error: any) {
      toast.error('删除失败', {
        description: error.message,
      });
    }
  };

  // 更新角色
  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      const response = await updateUserRole(selectedUser.id, newRole);
      if (response.success) {
        toast.success('角色已更新');
        setRoleDialogOpen(false);
        loadUsers();
        loadStats();
      }
    } catch (error: any) {
      toast.error('更新失败', {
        description: error.message,
      });
    }
  };

  // 更新状态
  const handleUpdateStatus = async () => {
    if (!selectedUser || !newStatus) return;

    try {
      const response = await updateUserStatus(selectedUser.id, newStatus);
      if (response.success) {
        toast.success('状态已更新');
        setStatusDialogOpen(false);
        loadUsers();
        loadStats();
      }
    } catch (error: any) {
      toast.error('更新失败', {
        description: error.message,
      });
    }
  };

  // 更新VIP状态
  const handleUpdateVip = async () => {
    if (!selectedUser) return;

    try {
      const response = await updateUserVip(selectedUser.id, {
        vip_level: newVipLevel,
        vip_expire_at: newVipExpireAt || null,
        vip_source: newVipSource || null,
      });
      if (response.success) {
        toast.success('VIP状态已更新');
        setVipDialogOpen(false);
        loadUsers();
        loadStats();
      }
    } catch (error: any) {
      toast.error('更新失败', {
        description: error.message,
      });
    }
  };

  // 创建用户
  const handleCreateUser = async () => {
    try {
      const response = await createUser(newUser);
      if (response.success) {
        toast.success('用户创建成功');
        setCreateDialogOpen(false);
        setNewUser({ username: '', email: '', password: '', role: 'user' });
        loadUsers();
        loadStats();
      }
    } catch (error: any) {
      toast.error('创建失败', {
        description: error.message,
      });
    }
  };

  // 获取角色徽章
  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Badge variant="destructive"><ShieldCheck className="w-3 h-3 mr-1" />超级管理员</Badge>;
      case 'admin':
        return <Badge variant="default"><Shield className="w-3 h-3 mr-1" />管理员</Badge>;
      case 'user':
        return <Badge variant="secondary"><UserIcon className="w-3 h-3 mr-1" />普通用户</Badge>;
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  // 获取状态徽章
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-600">激活</Badge>;
      case 'inactive':
        return <Badge variant="secondary">未激活</Badge>;
      case 'banned':
        return <Badge variant="destructive">已封禁</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // 获取VIP徽章
  const getVipBadge = (user: User) => {
    // 根据用户角色自动推断VIP等级（如果vip_level未设置）
    let effectiveVipLevel = user.vip_level ?? 0;
    
    // 如果用户是管理员角色但vip_level未设置为99，自动显示管理员等级
    if ((user.role === 'admin' || user.role === 'super_admin') && effectiveVipLevel < 99) {
      effectiveVipLevel = 99;
    }
    
    const vipInfo = getVipLevelInfo(effectiveVipLevel);
    const expired = isVipExpired(user.vip_expire_at);
    
    // 根据VIP等级显示不同颜色
    let badgeClass = '';
    switch (effectiveVipLevel) {
      case 1:
        badgeClass = expired ? 'bg-blue-300 text-blue-800' : 'bg-blue-600 text-white';
        break;
      case 2:
        badgeClass = expired ? 'bg-purple-300 text-purple-800' : 'bg-purple-600 text-white';
        break;
      case 99:
        badgeClass = 'bg-red-600 text-white';
        break;
      default:
        badgeClass = 'bg-gray-200 text-gray-700';
    }
    
    return (
      <div className="flex flex-col gap-1">
        <Badge variant="outline" className={badgeClass}>
          <Crown className="w-3 h-3 mr-1" />
          {vipInfo.name}
          {expired && effectiveVipLevel < 99 && ' (已过期)'}
        </Badge>
        {effectiveVipLevel > 0 && effectiveVipLevel < 99 && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatVipExpireAt(user.vip_expire_at)}
          </span>
        )}
      </div>
    );
  };

  // 获取 OneAPI 绑定状态显示
  const getOneAPIStatus = (user: User) => {
    if (user.oneapi_id === null || user.oneapi_id === undefined) {
      return <span className="text-sm text-muted-foreground">未绑定</span>;
    }
    return (
      <span className="text-sm text-green-600">已绑定 (ID: {user.oneapi_id})</span>
    );
  };

  // 格式化时间
  const formatTime = (time?: string) => {
    if (!time) return '-';
    return new Date(time).toLocaleString('zh-CN');
  };

  return (
    <LayoutWithSidebar title="用户管理">
      <div className="container mx-auto py-4 px-3 sm:py-8 sm:px-4 max-w-7xl">
        {/* 标题区域 */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">用户管理</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            管理系统用户账号、角色和权限
          </p>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">总用户数</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">管理员</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.admin_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">普通用户</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.user_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">激活</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.active_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">未激活</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">{stats.inactive_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">已封禁</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.banned_count}</div>
            </CardContent>
          </Card>
        </div>

        {/* VIP统计卡片 */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Crown className="w-4 h-4 text-gray-500" />
                免费用户
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">{stats.vip_free_count}</div>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Crown className="w-4 h-4 text-blue-500" />
                VIP会员
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.vip_vip_count}</div>
            </CardContent>
          </Card>
          <Card className="border-purple-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Crown className="w-4 h-4 text-purple-500" />
                VVIP会员
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{stats.vip_vvip_count}</div>
            </CardContent>
          </Card>
        </div>

        {/* 主内容卡片 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>用户列表</CardTitle>
                <CardDescription>查看和管理所有系统用户</CardDescription>
              </div>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                创建用户
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 搜索和筛选 */}
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <div className="flex-1 flex gap-2 min-w-[200px]">
                <Input
                  placeholder="搜索用户名或邮箱..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} variant="secondary">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="筛选角色" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">全部角色</SelectItem>
                  <SelectItem value="super_admin">超级管理员</SelectItem>
                  <SelectItem value="admin">管理员</SelectItem>
                  <SelectItem value="user">普通用户</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="筛选状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">全部状态</SelectItem>
                  <SelectItem value="active">激活</SelectItem>
                  <SelectItem value="inactive">未激活</SelectItem>
                  <SelectItem value="banned">已封禁</SelectItem>
                </SelectContent>
              </Select>
              <Select value={vipLevelFilter} onValueChange={setVipLevelFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="筛选VIP等级" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">全部VIP等级</SelectItem>
                  <SelectItem value="0">免费用户</SelectItem>
                  <SelectItem value="1">VIP会员</SelectItem>
                  <SelectItem value="2">VVIP会员</SelectItem>
                  <SelectItem value="99">管理员</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => { setRoleFilter(''); setStatusFilter(''); setVipLevelFilter(''); setKeyword(''); loadUsers(); }}>
                <RefreshCcw className="w-4 h-4" />
              </Button>
            </div>

            {/* 用户表格 */}
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">用户名</TableHead>
                    <TableHead className="min-w-[200px]">邮箱</TableHead>
                    <TableHead>角色</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead className="min-w-[120px]">VIP等级</TableHead>
                    <TableHead className="min-w-[120px]">OneAPI</TableHead>
                    <TableHead className="min-w-[160px]">最后登录时间</TableHead>
                    <TableHead>最后登录IP</TableHead>
                    <TableHead className="min-w-[160px]">注册时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-8">
                        加载中...
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                        暂无用户数据
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.username}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{getRoleBadge(user.role)}</TableCell>
                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell>{getVipBadge(user)}</TableCell>
                        <TableCell>{getOneAPIStatus(user)}</TableCell>
                        <TableCell>
                          {user.last_login_at ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Clock className="w-3 h-3 text-muted-foreground" />
                              {formatTime(user.last_login_at)}
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {user.last_login_ip ? (
                            <div className="flex items-center gap-1 text-sm">
                              <MapPin className="w-3 h-3 text-muted-foreground" />
                              {user.last_login_ip}
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatTime(user.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2 flex-wrap">
                            {/* 只有超级管理员可以修改管理员角色，管理员只能修改普通用户角色 */}
                            {canManageUser(user) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setNewRole(user.role);
                                  setRoleDialogOpen(true);
                                }}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                角色
                              </Button>
                            )}
                            {/* 只有超级管理员可以修改管理员状态，管理员只能修改普通用户状态 */}
                            {canManageUser(user) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setNewStatus(user.status);
                                  setStatusDialogOpen(true);
                                }}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                状态
                              </Button>
                            )}
                            {/* VIP状态修改按钮 */}
                            {canManageUser(user) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setNewVipLevel(user.vip_level || 0);
                                  setNewVipExpireAt(user.vip_expire_at ? new Date(user.vip_expire_at).toISOString().split('T')[0] : '');
                                  setNewVipSource(user.vip_source || '');
                                  setVipDialogOpen(true);
                                }}
                              >
                                <Crown className="w-3 h-3 mr-1" />
                                VIP
                              </Button>
                            )}
                            {/* 只有超级管理员可以删除管理员，管理员只能删除普通用户 */}
                            {canManageUser(user) && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                            {/* 如果没有权限操作，显示提示 */}
                            {!canManageUser(user) && user.id !== currentUser?.id && (
                              <span className="text-xs text-muted-foreground px-2">无权限操作</span>
                            )}
                            {user.id === currentUser?.id && (
                              <span className="text-xs text-muted-foreground px-2">不能操作自己</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* 分页 */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                共 {total} 个用户，第 {page} / {Math.ceil(total / pageSize) || 1} 页
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(total / pageSize)}
                >
                  下一页
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 创建用户对话框 */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>创建新用户</DialogTitle>
              <DialogDescription>添加一个新的用户账号到系统</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="username">用户名 *</Label>
                <Input
                  id="username"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="至少3个字符"
                />
              </div>
              <div>
                <Label htmlFor="email">邮箱 *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <Label htmlFor="password">密码 *</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="至少6个字符"
                />
              </div>
              <div>
                <Label htmlFor="role">角色</Label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">普通用户</SelectItem>
                    {/* 管理员及以上可以创建管理员 */}
                    {isAdmin && <SelectItem value="admin">管理员</SelectItem>}
                    {isSuperAdmin && <SelectItem value="super_admin">超级管理员</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                取消
              </Button>
              <Button onClick={handleCreateUser}>创建</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 修改角色对话框 */}
        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>修改用户角色</DialogTitle>
              <DialogDescription>
                修改用户 <strong>{selectedUser?.username}</strong> 的角色
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newRole">选择新角色</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">普通用户</SelectItem>
                    {/* 管理员及以上可以设置管理员角色 */}
                    {isAdmin && <SelectItem value="admin">管理员</SelectItem>}
                    {/* 只有超级管理员可以设置超级管理员角色 */}
                    {isSuperAdmin && <SelectItem value="super_admin">超级管理员</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
                取消
              </Button>
              <Button onClick={handleUpdateRole}>确认</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 修改状态对话框 */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>修改用户状态</DialogTitle>
              <DialogDescription>
                修改用户 <strong>{selectedUser?.username}</strong> 的状态
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newStatus">选择新状态</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">激活</SelectItem>
                    <SelectItem value="inactive">未激活</SelectItem>
                    <SelectItem value="banned">已封禁</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                取消
              </Button>
              <Button onClick={handleUpdateStatus}>确认</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 修改VIP状态对话框 */}
        <Dialog open={vipDialogOpen} onOpenChange={setVipDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-purple-600" />
                修改VIP状态
              </DialogTitle>
              <DialogDescription>
                修改用户 <strong>{selectedUser?.username}</strong> 的VIP等级和有效期
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newVipLevel">VIP等级</Label>
                <Select 
                  value={newVipLevel.toString()} 
                  onValueChange={(value) => setNewVipLevel(Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">免费用户</SelectItem>
                    <SelectItem value="1">VIP会员</SelectItem>
                    <SelectItem value="2">VVIP会员</SelectItem>
                    {isSuperAdmin && <SelectItem value="99">管理员</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="newVipExpireAt">过期时间（留空表示永久）</Label>
                <Input
                  id="newVipExpireAt"
                  type="date"
                  value={newVipExpireAt}
                  onChange={(e) => setNewVipExpireAt(e.target.value)}
                  placeholder="选择过期时间"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  留空表示永久有效
                </p>
              </div>
              <div>
                <Label htmlFor="newVipSource">VIP来源（可选）</Label>
                <Select 
                  value={newVipSource} 
                  onValueChange={setNewVipSource}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择或留空" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">无</SelectItem>
                    <SelectItem value="ring_299">智能戒指(基础版) ¥299</SelectItem>
                    <SelectItem value="gateway_399">智能网关 ¥399</SelectItem>
                    <SelectItem value="ring_599">智能戒指(高级版) ¥599</SelectItem>
                    <SelectItem value="combo_any">戒指+网关组合</SelectItem>
                    <SelectItem value="admin_grant">管理员赠送</SelectItem>
                    <SelectItem value="promotion">活动赠送</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVipDialogOpen(false)}>
                取消
              </Button>
              <Button onClick={handleUpdateVip} className="bg-purple-600 hover:bg-purple-700">
                确认
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 删除确认对话框 */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>确认删除用户</AlertDialogTitle>
              <AlertDialogDescription>
                您确定要删除用户 <strong>{selectedUser?.username}</strong> 吗？
                此操作无法撤销，该用户的所有数据都将被删除。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>取消</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                确认删除
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </LayoutWithSidebar>
  );
}
